import { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bug, Lightbulb, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import TurnstileWidget from "@/components/TurnstileWidget";
import { useSeo } from "@/seo";

export default function FeedbackPage() {
  const { t } = useI18n();
  useSeo(t("feedback.seo_title"), t("feedback.seo_desc"), "/feedback");
  const [kind, setKind] = useState("bug");
  // Signalement venu d'une fiche joueur : le nom est pré-rempli dans le message et la fiche
  // voyage avec le rapport. Sans ça, « le nom est faux » est inexploitable (24/09/2026).
  const [params] = useSearchParams();
  const playerId = params.get("player");
  const playerName = params.get("name");
  const [message, setMessage] = useState(
    playerName ? t("feedback.player_prefill", { name: playerName }) : "");  const [email, setEmail] = useState("");
  const [token, setToken] = useState(null);
  const widget = useRef(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onToken = useCallback((v) => setToken(v), []);
  const onInvalid = useCallback(() => setToken(null), []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/feedback", { kind, message, email: email || null, page: window.location.pathname,
        player_id: playerId ? Number(playerId) : null, provider: playerId ? (params.get("p") || "api-football") : null,
        turnstile_token: token });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail === "too_many_reports" ? t("feedback.too_many") : formatApiError(err));
    } finally {
      setBusy(false);
      widget.current?.reset(); // single-use token: reset after every submission
      setToken(null);
    }
  };

  const inputCls = "w-full rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2.5 text-sm text-slate-200 focus:border-[#00FF87] focus:outline-none";

  if (done) {
    return (
      <div className="mx-auto max-w-xl pt-16" data-testid="feedback-done">
        <h1 className="font-display text-3xl font-black uppercase tracking-tight text-slate-50">{t("feedback.thanks_title")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{t("feedback.thanks_body")}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl pt-10" data-testid="feedback-page">
      <h1 className="font-display text-3xl font-black uppercase tracking-tight text-slate-50 sm:text-4xl">{t("feedback.title")}</h1>
      <p className="mt-3 text-sm leading-relaxed text-slate-400">{t("feedback.intro")}</p>

      <form onSubmit={submit} className="hud-card mt-6 space-y-4 rounded-xl p-5" data-testid="feedback-form">
        <div className="grid grid-cols-2 gap-3">
          {[["bug", Bug], ["improvement", Lightbulb]].map(([k, Icon]) => (
            <button
              key={k}
              type="button"
              data-testid={`feedback-kind-${k}`}
              onClick={() => setKind(k)}
              className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 font-mono2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                kind === k ? "border-[#00FF87] bg-[#00FF87]/10 text-[#00FF87]" : "border-[#1F293D] text-slate-400 hover:text-slate-200"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {t(`feedback.kind_${k}`)}
            </button>
          ))}
        </div>

        <div>
          <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("feedback.message_label")}</label>
          <textarea
            data-testid="feedback-message-input"
            required
            minLength={10}
            maxLength={4000}
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={kind === "bug" ? t("feedback.placeholder_bug") : t("feedback.placeholder_idea")}
            className={inputCls}
          />
          <p className="mt-1 font-mono2 text-[9px] uppercase tracking-wider text-slate-600">{message.length}/4000</p>
        </div>

        <div>
          <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("feedback.email_label")}</label>
          <input data-testid="feedback-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <p className="mt-1 text-[11px] leading-snug text-slate-500">{t("feedback.email_hint")}</p>
        </div>

        <TurnstileWidget ref={widget} action="feedback" onToken={onToken} onInvalid={onInvalid} />

        <button
          type="submit"
          data-testid="feedback-submit-button"
          disabled={busy || !token || message.trim().length < 10}
          className="btn-volt flex w-full items-center justify-center gap-2 disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" /> {busy ? t("common.loading") : t("feedback.submit")}
        </button>
      </form>
    </div>
  );
}
